import { DeleteOutline, MoreVert } from '@mui/icons-material';
import { Button, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';

import type { ProjectRunAnalysisQuestion } from '../../../Types/dealGPT';
import { ProcessReason, UpdatedReason } from '../../../Types/enums';
import SectionButtons from './SectionButtons';
interface MoreOptionsMenuProps {
  projectId: string;
  handleSave: (
    values: Record<string, string>,
    reason?: UpdatedReason,
    processReason?: ProcessReason
  ) => Promise<void>;
  values: Record<string, string>;
  isEditing?: boolean;
  analysisQuestionData: ProjectRunAnalysisQuestion;
  handleValidateFindingsClick: (questionId: string) => void;
  canDelete?: boolean;
  handleDelete?: () => void;
}

const MoreOptionsMenu: React.FC<MoreOptionsMenuProps> = ({
  projectId,
  handleSave,
  values,
  isEditing = true,
  analysisQuestionData,
  handleValidateFindingsClick,
  canDelete = false,
  handleDelete,
}) => {
  const [anchorContextMenu, setAnchorContextMenu] = useState<null | HTMLElement>(null);
  const handleOpenContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorContextMenu(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorContextMenu(null);
  };

  const onDelete = useCallback(() => {
    if (handleDelete) {
      handleDelete();
    }
    setAnchorContextMenu(null);
  }, [handleDelete]);

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      style={{ display: 'flex', flexDirection: 'column', width: '30px' }}
    >
      <Tooltip
        id={`open-settings-label-${analysisQuestionData?.id}`}
        title={<Typography variant="caption"> More Options </Typography>}
        PopperProps={{ keepMounted: true }}
      >
        <IconButton
          sx={{ maxHeight: '36px' }}
          onClick={handleOpenContextMenu}
          aria-labelledby={`open-settings-label-${analysisQuestionData?.id}`}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        sx={{ mt: '32px' }}
        anchorEl={anchorContextMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={Boolean(anchorContextMenu)}
        onClose={handleCloseUserMenu}
      >
        <MenuItem>
          <SectionButtons
            projectId={projectId}
            handleSave={handleSave}
            values={values}
            hasGenerated={true}
            isEditing={isEditing}
            question={analysisQuestionData}
            showGenerateButton={true}
            showEditButton={false}
            showSaveButton={false}
            isMountedOnMenu={true}
            handleCloseMenu={handleCloseUserMenu}
            showValidateButton={false}
          />
        </MenuItem>
        <MenuItem>
          <SectionButtons
            projectId={projectId}
            handleSave={handleSave}
            values={values}
            hasGenerated={true}
            isEditing={false}
            question={analysisQuestionData}
            showGenerateButton={false}
            showEditButton={false}
            showSaveButton={false}
            isMountedOnMenu={true}
            handleCloseMenu={handleCloseUserMenu}
            showValidateButton={true}
            handleValidate={handleValidateFindingsClick}
          />
        </MenuItem>
        {canDelete && (
          <MenuItem disabled={!handleDelete}>
            <Tooltip
              title={
                !handleDelete ? 'This is a Project Level KPI and therefore cannot be deleted' : ''
              }
              placement="left"
              arrow
            >
              <span style={{ display: 'inline-block', pointerEvents: 'auto' }}>
                {/* Ensures Tooltip works */}
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<DeleteOutline />}
                  disabled={!handleDelete}
                  onClick={onDelete}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'transparent !important', // Disable hover effect on Button
                    },
                  }}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default MoreOptionsMenu;
